// components/DegreeAudit/Common/GraduationGates.tsx
//
// Graduation-readiness strip. Renders the audit response's
// `graduation_readiness` payload as a compact row of progress "gates"
// (credits / residency / GPA).
//
// Design intent — this REPLACES the old always-on amber residency warning
// for the common in-progress case. End-state requirements (residency, GPA,
// total credits) are unmet for almost the entire life of a plan, so showing
// them as red/amber warnings from day one is alarm fatigue. Here they read
// as neutral progress and only turn amber when a gate is genuinely AT RISK
// (residency no longer reachable; GPA below the bar). The precise, actionable
// warning still lives in <ConstraintWarnings/> when a gate is unreachable.

import { memo } from 'react';
import { GraduationCap } from 'lucide-react';
import { useCreditUnit } from '../../../contexts/InstitutionConfigContext';

export interface CreditGate {
  committed: number;
  required: number | null;
}

export interface ResidencyGate {
  committed: number;
  required: number | null;
  reachable: boolean;
  achievable_max: number;
  scope: string;
  source: string;
}

export interface GpaGate {
  current: number | null;
  /** Cumulative GPA over all graded courses (incl. in-progress/future terms
   *  with entered grades). Shown when `current` is null so a plan with no
   *  completed terms still surfaces a GPA instead of a permanent dash. */
  projected?: number | null;
  required: number | null;
  /** Top of the institution's GPA scale (4.0, 10.0, …), from its grade
   *  scale. Absent/null → the value is shown without a ceiling; the UI never
   *  assumes 4.0, because not every institution grades on a 4-point scale. */
  scale_max?: number | null;
}

export interface GraduationReadiness {
  credits: CreditGate;
  residency: ResidencyGate;
  gpa: GpaGate;
}

export interface GraduationGatesProps {
  readiness: GraduationReadiness | null | undefined;
}

type Tone = 'neutral' | 'good' | 'risk';

const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-gray-600',
  good: 'text-emerald-700',
  risk: 'text-amber-700',
};

const TONE_BAR: Record<Tone, string> = {
  neutral: 'bg-gray-400',
  good: 'bg-emerald-500',
  risk: 'bg-amber-500',
};

interface GateView {
  key: string;
  label: string;
  valueText: string;
  tone: Tone;
  ratio: number | null; // 0..1 for a progress bar; null = value-only chip
  /** Neutral secondary line under the value (e.g. the GPA minimum). */
  detail?: string;
  /** Amber at-risk line (e.g. residency no longer reachable). */
  note?: string;
  /** Hover explanation for the whole cell. The label carries the essential
   *  meaning on its own — this only adds detail — because tooltips are
   *  unreachable on touch. */
  title?: string;
}

function fmtGpa(v: number): string {
  return v.toFixed(2);
}

/** Build the renderable gate list, skipping gates with no requirement. */
function buildGates(r: GraduationReadiness, creditsLabel: string): GateView[] {
  const gates: GateView[] = [];

  // Credits — neutral until met, then green. Never "at risk" on its own.
  if (r.credits?.required != null) {
    const { committed, required } = r.credits;
    const met = committed >= required;
    gates.push({
      key: 'credits',
      label: creditsLabel,
      valueText: `${committed} / ${required}`,
      tone: met ? 'good' : 'neutral',
      ratio: required > 0 ? Math.min(1, committed / required) : 0,
    });
  }

  // Residency — the key gate. Neutral while reachable, amber when not.
  if (r.residency?.required != null) {
    const { committed, required, reachable } = r.residency;
    const met = committed >= required;
    const tone: Tone = met ? 'good' : reachable ? 'neutral' : 'risk';
    gates.push({
      key: 'residency',
      label: 'In-residence',
      valueText: `${committed} / ${required}`,
      tone,
      ratio: required > 0 ? Math.min(1, committed / required) : 0,
      note: !met && !reachable ? `max ${r.residency.achievable_max} reachable` : undefined,
    });
  }

  // GPA — value chip (no bar; a fill against a 2.0 floor reads oddly).
  // Earned (`current`) wins; fall back to `projected` (labelled) so a plan
  // with entered grades but no completed terms shows a GPA, not a dash.
  //
  // UX-R1 round 2: the label says "Cumulative" outright. Round 1 labelled the
  // per-PROGRAM chip ("Major GPA", task 1.7) but left this one as a bare
  // "GPA" — which is the number the reviewer actually asked about ("is that
  // cumulative?"). The word carries the answer; the title only adds the floor.
  //
  // Reading: "3.60 / 4.00" with "Minimum 2.00" on a muted line beneath. The
  // denominator is the TOP OF THE SCALE (from the institution's grade scale —
  // 4.00, 10.00, …), never the graduation minimum: "3.60 / 2.00" read as a
  // GPA out of 2. The minimum gets its own line so it cannot be mistaken for
  // the scale. No scale_max (an older cached audit, or a scale without
  // points) → the value stands alone; 4.0 is never assumed.
  if (r.gpa?.required != null) {
    const { current, projected, required } = r.gpa;
    const scaleMax = r.gpa.scale_max ?? null;
    const value = current ?? projected ?? null;
    const outOf = (v: string) => (scaleMax != null ? `${v} / ${fmtGpa(scaleMax)}` : v);
    const detail = `Minimum ${fmtGpa(required)}`;
    const scaleClause = scaleMax != null ? ` on a ${fmtGpa(scaleMax)} scale` : '';
    const gpaTitle =
      `Cumulative GPA across all graded courses${scaleClause}. ` +
      `${fmtGpa(required)} required to graduate.`;
    if (value == null) {
      gates.push({
        key: 'gpa',
        label: 'Cumulative GPA',
        valueText: outOf('—'),
        tone: 'neutral',
        ratio: null,
        detail,
        title: gpaTitle,
      });
    } else {
      const projectedOnly = current == null;
      gates.push({
        key: 'gpa',
        label: projectedOnly ? 'Cumulative GPA (proj.)' : 'Cumulative GPA',
        valueText: outOf(fmtGpa(value)),
        tone: value >= required ? 'good' : 'risk',
        ratio: null,
        detail,
        title: projectedOnly
          ? `${gpaTitle} Projected from planned courses — no completed terms yet.`
          : gpaTitle,
      });
    }
  }

  return gates;
}

const GraduationGates = memo(({ readiness }: GraduationGatesProps) => {
  const creditUnit = useCreditUnit();
  if (!readiness) return null;

  const gates = buildGates(readiness, creditUnit.heading);
  if (gates.length === 0) return null;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4">
      <h3 className="font-medium text-gray-700 mb-2 md:mb-3 flex items-center text-sm">
        <GraduationCap className="w-4 h-4 mr-1 md:mr-2 print-hide" />
        Graduation gates
      </h3>

      {/* Columns follow the strip's own width, not the viewport: the audit
          column is narrow on a wide screen, and a viewport breakpoint gave it
          three columns too thin for "Cumulative GPA — / 4.00", which pushed
          the value past the card's edge. auto-fit drops to two columns (or
          one) when three do not fit. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-3 md:gap-4">
        {gates.map((g) => (
          <div key={g.key} title={g.title} className="min-w-0">
            {/* The reading "3.42 / 4.00" never breaks (nowrap), so when the
                cell is too narrow for label and value side by side, the value
                wraps under the label instead of overflowing. */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-2">
              <span className="text-xs text-gray-500">{g.label}</span>
              <span
                className={`text-xs font-medium whitespace-nowrap ${TONE_TEXT[g.tone]}`}
              >
                {g.valueText}
              </span>
            </div>
            {g.ratio != null && (
              <div className="w-full bg-surface-sunken rounded-full h-1.5 mt-1">
                <div
                  className={`${TONE_BAR[g.tone]} h-1.5 rounded-full transition-all`}
                  style={{ width: `${Math.round(g.ratio * 100)}%` }}
                />
              </div>
            )}
            {g.detail && (
              <div className="text-xs text-ink-3 mt-0.5">{g.detail}</div>
            )}
            {g.note && (
              <div className="text-xs text-amber-600 mt-1">{g.note}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

GraduationGates.displayName = 'GraduationGates';

export default GraduationGates;
